/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { OemAssetStatus, OemAssetType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface OemAssetProps {
  id: string;
  oemPlanId: string;
  assetId: string;
  assetType: OemAssetType;
  assetDispatchCompanyId: string | null;
  status: OemAssetStatus;
  originalUnitId: string | null;
  oemUnitId: string | null;
  requestedPlacardNumber: string | null;
  requestedUnitName: string | null;
  requestedUnitAbbreviation: string | null;
  requestedBaseId: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class OemAssetRecord extends Record<OemAssetProps>({
  id: null as any,
  oemPlanId: null as any,
  assetId: null as any,
  assetType: null as any,
  assetDispatchCompanyId: null as any,
  status: null as any,
  originalUnitId: null as any,
  oemUnitId: null as any,
  requestedPlacardNumber: null as any,
  requestedUnitName: null as any,
  requestedUnitAbbreviation: null as any,
  requestedBaseId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    oemPlanId: string;
    assetId: string;
    assetType: OemAssetType;
    assetDispatchCompanyId: string | null;
    status: OemAssetStatus;
    originalUnitId: string | null;
    oemUnitId: string | null;
    requestedPlacardNumber: string | null;
    requestedUnitName: string | null;
    requestedUnitAbbreviation: string | null;
    requestedBaseId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): OemAssetRecord {
    if (json == null) {
      throw new Error('OemAssetRecord.fromJS: parameter cannot be null');
    }
    return new OemAssetRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'OemAssetJson.id',
      ),
      oemPlanId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'oemPlanId', '00000000-0000-0000-0000-000000000000'),
        'OemAssetJson.oemPlanId',
      ),
      assetId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'assetId', '00000000-0000-0000-0000-000000000000'),
        'OemAssetJson.assetId',
      ),
      assetType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'assetType', OemAssetType.Vehicle),
        'OemAssetJson.assetType',
      ),
      assetDispatchCompanyId: json.assetDispatchCompanyId,
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', OemAssetStatus.Standby),
        'OemAssetJson.status',
      ),
      originalUnitId: json.originalUnitId,
      oemUnitId: json.oemUnitId,
      requestedPlacardNumber: json.requestedPlacardNumber,
      requestedUnitName: json.requestedUnitName,
      requestedUnitAbbreviation: json.requestedUnitAbbreviation,
      requestedBaseId: json.requestedBaseId,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    oemPlanId: string;
    assetId: string;
    assetType: OemAssetType;
    assetDispatchCompanyId: string | null;
    status: OemAssetStatus;
    originalUnitId: string | null;
    oemUnitId: string | null;
    requestedPlacardNumber: string | null;
    requestedUnitName: string | null;
    requestedUnitAbbreviation: string | null;
    requestedBaseId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [OemAssetRecord, Error[]] {
    if (json == null) {
      throw new Error('OemAssetRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new OemAssetRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'OemAssetJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      oemPlanId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'oemPlanId', '00000000-0000-0000-0000-000000000000'),
            'OemAssetJson.oemPlanId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      assetId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'assetId', '00000000-0000-0000-0000-000000000000'),
            'OemAssetJson.assetId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      assetType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'assetType', OemAssetType.Vehicle),
            'OemAssetJson.assetType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      assetDispatchCompanyId: tryOrDefault(
        () => json.assetDispatchCompanyId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', OemAssetStatus.Standby),
            'OemAssetJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      originalUnitId: tryOrDefault(
        () => json.originalUnitId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      oemUnitId: tryOrDefault(
        () => json.oemUnitId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestedPlacardNumber: tryOrDefault(
        () => json.requestedPlacardNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestedUnitName: tryOrDefault(
        () => json.requestedUnitName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestedUnitAbbreviation: tryOrDefault(
        () => json.requestedUnitAbbreviation,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestedBaseId: tryOrDefault(
        () => json.requestedBaseId,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: OemAssetProps) {
    super(props);
    if (props == null) {
      throw new Error('OemAssetRecord constructor parameter cannot be null');
    }
  }
}

export abstract class OemAssetViewModelBase implements OemAssetProps {
  protected readonly _props: OemAssetProps;
  private _json?: OemAssetProps;

  get id(): string {
    return this._props.id;
  }
  get oemPlanId(): string {
    return this._props.oemPlanId;
  }
  get assetId(): string {
    return this._props.assetId;
  }
  get assetType(): OemAssetType {
    return this._props.assetType;
  }
  get assetDispatchCompanyId(): string | null {
    return this._props.assetDispatchCompanyId;
  }
  get status(): OemAssetStatus {
    return this._props.status;
  }
  get originalUnitId(): string | null {
    return this._props.originalUnitId;
  }
  get oemUnitId(): string | null {
    return this._props.oemUnitId;
  }
  get requestedPlacardNumber(): string | null {
    return this._props.requestedPlacardNumber;
  }
  get requestedUnitName(): string | null {
    return this._props.requestedUnitName;
  }
  get requestedUnitAbbreviation(): string | null {
    return this._props.requestedUnitAbbreviation;
  }
  get requestedBaseId(): string | null {
    return this._props.requestedBaseId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isPersonal() {
    return this._props.assetType === OemAssetType.Personal;
  }
  public get isVehicle() {
    return this._props.assetType === OemAssetType.Vehicle;
  }
  public get isActivated() {
    return this._props.status === OemAssetStatus.Activated;
  }
  public get isDemobilized() {
    return this._props.status === OemAssetStatus.Demobilized;
  }
  public get isStandby() {
    return this._props.status === OemAssetStatus.Standby;
  }

  public isAnyAssetType(...oemAssetType: OemAssetType[]) {
    return oemAssetType.includes(this._props.assetType);
  }
  public isAnyStatus(...oemAssetStatus: OemAssetStatus[]) {
    return oemAssetStatus.includes(this._props.status);
  }

  constructor(props: OemAssetProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        oemPlanId: this._props.oemPlanId,
        assetId: this._props.assetId,
        assetType: this._props.assetType,
        assetDispatchCompanyId: this._props.assetDispatchCompanyId,
        status: this._props.status,
        originalUnitId: this._props.originalUnitId,
        oemUnitId: this._props.oemUnitId,
        requestedPlacardNumber: this._props.requestedPlacardNumber,
        requestedUnitName: this._props.requestedUnitName,
        requestedUnitAbbreviation: this._props.requestedUnitAbbreviation,
        requestedBaseId: this._props.requestedBaseId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllOemAssetEntities = (state: AppState) => state.entityStores.oemAsset.entities;

export const selectOemAssetById = (id: string) => (state: AppState) =>
  state.entityStores.oemAsset.entities.get(id);

export const selectOemAssetMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.oemAsset.meta.get(id);

export const selectOemAssetAndMetadataById = (id: string) =>
  createSelector(selectOemAssetById(id), selectOemAssetMetadataById(id), (oemAsset, meta) => ({
    oemAsset,
    meta,
  }));

export function selectOemAssetEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllOemAssetEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, OemAssetRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectOemAssetEntitiesByOemPlanId(oemPlanId: string) {
  return createSelector(selectAllOemAssetEntities, (entities) =>
    entities.filter((e) => e.oemPlanId === oemPlanId).valueSeq(),
  );
}

export function selectOemAssetEntitiesByAssetId(assetId: string) {
  return createSelector(selectAllOemAssetEntities, (entities) =>
    entities.filter((e) => e.assetId === assetId).valueSeq(),
  );
}

export function selectOemAssetEntitiesByAssetType(assetType: OemAssetType) {
  return createSelector(selectAllOemAssetEntities, (entities) =>
    entities.filter((e) => e.assetType === assetType).valueSeq(),
  );
}

export function selectOemAssetEntitiesByAssetDispatchCompanyId(assetDispatchCompanyId: string) {
  return createSelector(selectAllOemAssetEntities, (entities) =>
    entities.filter((e) => e.assetDispatchCompanyId === assetDispatchCompanyId).valueSeq(),
  );
}

export function selectOemAssetEntitiesByStatus(status: OemAssetStatus) {
  return createSelector(selectAllOemAssetEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectOemAssetEntitiesByOriginalUnitId(originalUnitId: string) {
  return createSelector(selectAllOemAssetEntities, (entities) =>
    entities.filter((e) => e.originalUnitId === originalUnitId).valueSeq(),
  );
}

export function selectOemAssetEntitiesByOemUnitId(oemUnitId: string) {
  return createSelector(selectAllOemAssetEntities, (entities) =>
    entities.filter((e) => e.oemUnitId === oemUnitId).valueSeq(),
  );
}

export function selectOemAssetEntitiesByRequestedPlacardNumber(requestedPlacardNumber: string) {
  return createSelector(selectAllOemAssetEntities, (entities) =>
    entities.filter((e) => e.requestedPlacardNumber === requestedPlacardNumber).valueSeq(),
  );
}

export function selectOemAssetEntitiesByRequestedUnitName(requestedUnitName: string) {
  return createSelector(selectAllOemAssetEntities, (entities) =>
    entities.filter((e) => e.requestedUnitName === requestedUnitName).valueSeq(),
  );
}

export function selectOemAssetEntitiesByRequestedUnitAbbreviation(
  requestedUnitAbbreviation: string,
) {
  return createSelector(selectAllOemAssetEntities, (entities) =>
    entities.filter((e) => e.requestedUnitAbbreviation === requestedUnitAbbreviation).valueSeq(),
  );
}

export function selectOemAssetEntitiesByRequestedBaseId(requestedBaseId: string) {
  return createSelector(selectAllOemAssetEntities, (entities) =>
    entities.filter((e) => e.requestedBaseId === requestedBaseId).valueSeq(),
  );
}
//#endregion Selectors
